import React, { memo } from 'react';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { CBody } from '@modules/common/CBody';
import { FormInput, FormCheckbox, FormColorPicker } from '@core/components/form';
import { WidgetConfirmProps } from '../types';
import { CardTitle, Col, Row } from 'reactstrap';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { RepeatingForm, RepeatingFormControls, RepeatingFormItem } from '@modules/common';
import { Button } from '@core/components/Button';

export function defineColorForm() {
  return formGroup({
    label: formControl<string>({ label: 'Label' }),
    color: formControl<string>({ label: 'Color' }),
  });
}

type ColorFormType = ReturnType<typeof defineColorForm>;

export function defineLineChartWidgetConfirmForm() {
  return formGroup({
    individual: formControl<boolean>({ label: 'Show Individual Points', initialValue: false }),
    showZeros: formControl<boolean>({ label: 'Show zero for empty dates', initialValue: false }),
    upside: formControl<boolean>({ label: 'Upside down graph', initialValue: false }),
    isRelativeDate: formControl<boolean>({ label: 'Relative Date', initialValue: false }),
    relativeDays: formControl<string>({ label: 'Number of Days' }),
    noRedBlackLines: formControl<boolean>({ label: "Don't use Red Black Lines", initialValue: false }),
    showValuesOnGraph: formControl<boolean>({ label: 'Show values on the graph', initialValue: false }),
    onlyOperationDays: formControl<boolean>({ label: 'Exclude non operational days', initialValue: false }),
    legends: formControl<boolean>({ label: 'Show Legends', initialValue: false }),
    colors: formArray(() => defineColorForm()),
  });
}

export type LineChartWidgetConfigType = ReturnType<typeof defineLineChartWidgetConfirmForm>;
export type LineChartWidgetConfigData = FormDataType<typeof defineLineChartWidgetConfirmForm>;

export const LineChartWidgetConfig = memo((props: WidgetConfirmProps<LineChartWidgetConfigType, LineChartWidgetConfigData>) => {
  const { form, data } = props;
  const formValue = useNonNilObservable(form.value$);
  return (
    <CBody>
      <CardTitle>Widget Settings</CardTitle>
      <FormCheckbox control={form.controls.individual} />
      <FormCheckbox control={form.controls.onlyOperationDays} />
      <FormCheckbox control={form.controls.noRedBlackLines} />
      <FormCheckbox control={form.controls.showZeros} />
      <FormCheckbox control={form.controls.upside} />
      <FormCheckbox control={form.controls.legends} />
      <FormCheckbox control={form.controls.showValuesOnGraph} />
      <FormCheckbox control={form.controls.isRelativeDate} />
      {!!formValue.isRelativeDate && <FormInput control={form.controls.relativeDays} />}
      {formValue.colors.length === 0 && (
        <Button className="mt-2" onClick={() => form.controls.colors.push()}>
          Add Color
        </Button>
      )}
      <RepeatingForm form={form.controls.colors} render={(item, index) => <ColorForm key={index} item={item} />} />
    </CBody>
  );
});

interface ColorFormProps {
  item: RepeatingFormItem<ColorFormType>;
}

const ColorForm = memo((props: ColorFormProps) => {
  const { item } = props;
  const { form, repeatingControlsProps } = item;

  return (
    <Row className="flex-fill">
      <Col md={6} className="d-flex">
        <FormInput control={form.controls.label} />
      </Col>
      <Col md={6} className="d-flex">
        <FormColorPicker control={form.controls.color} />
        <RepeatingFormControls {...repeatingControlsProps} />
      </Col>
    </Row>
  );
});
